import { useAuth, useOptionalAuth } from '@/providers/AuthProvider'
import { useOptionalTenant, useTenant } from '@/providers/TenantProvider'

/**
 * Permission keys mirrored from the backend catalog (src/auth/rbac/permissions.ts).
 * Used for route guards and nav gating so we avoid stringly-typed typos.
 */
export const PERMISSIONS = {
  dashboardView: 'dashboard.view',
  formsRead: 'forms.read',
  formsCreate: 'forms.create',
  formsEdit: 'forms.edit',
  formsDelete: 'forms.delete',
  submissionsRead: 'submissions.read',
  submissionsCreate: 'submissions.create',
  submissionsEdit: 'submissions.edit',
  // Completing a form; also what skipping and unskipping one in a run needs.
  submissionsComplete: 'submissions.complete',
  submissionsUnlock: 'submissions.unlock',
  // Remove an applied signature from a field (z8nrz7dt40). In no system role
  // preset: admins hold it through full access, QA through a custom role.
  signaturesUnsign: 'signatures.unsign',
  // Sign a Review field assigned to you (z8nrz7dt41). In no system role
  // preset, like unlock: filling a form is not the right to review it.
  submissionsReview: 'submissions.review',
  procedureTemplatesRead: 'procedure_templates.read',
  procedureTemplatesManage: 'procedure_templates.manage',
  proceduresRead: 'procedures.read',
  proceduresManage: 'procedures.manage',
  // POST /procedures: starting a run. Also opens the MES Product list for its picker (z8nrz7fvhx).
  proceduresSchedule: 'procedures.schedule',
  // Correct a patient's demographics (z8nrz7dp78).
  patientsEdit: 'patients.edit',
  proceduresAssign: 'procedures.assign',
  integrationsRead: 'integrations.read',
  integrationsManage: 'integrations.manage',
  // Integrations → Printers (z8nrz7evkc): add, edit, approve and retire label
  // templates and printers, and send a test print. Reading needs integrations.read.
  labelPrintingManage: 'label_printing.manage',
  sourcesManage: 'sources.manage',
  // Every audit trail, plus the audit pop-ups on records.
  auditRead: 'audit.read',
  // One Audit Trail Center chip each, so a custom role can grant a single trail.
  auditUserRead: 'audit.user.read',
  auditStorageRead: 'audit.storage.read',
  auditProductRead: 'audit.product.read',
  auditInventoryRead: 'audit.inventory.read',
  auditEquipmentRead: 'audit.equipment.read',
  auditProcedureRead: 'audit.procedure.read',
  auditFieldRead: 'audit.field.read',
  auditPatientRead: 'audit.patient.read',
  auditLabelsRead: 'audit.labels.read',
  auditOfflineRead: 'audit.offline.read',
  auditExport: 'audit.export',
  usersManage: 'users.manage',
  rolesRead: 'roles.read',
  rolesManage: 'roles.manage',
  rolesDelete: 'roles.delete',
  fieldsRead: 'fields.read',
  fieldsCreate: 'fields.create',
  fieldsEdit: 'fields.edit',
  fieldsDelete: 'fields.delete',
  processRead: 'process.read',
  processCreate: 'process.create',
  processEdit: 'process.edit',
  processDelete: 'process.delete',
  processExecute: 'process.execute',
  processAssign: 'process.assign',
  // Tenant-scoped (z8nrz7e6zn): read opens Configure → Partners (Customers until
  // z8nrz7earp), manage edits it. The keys keep the customer name.
  customersRead: 'customers.read',
  customersManage: 'customers.manage',
  optionSetsRead: 'option_sets.read',
  optionSetsManage: 'option_sets.manage',
  resourceCapabilitiesRead: 'resource_capabilities.read',
  resourceCapabilitiesManage: 'resource_capabilities.manage',
  // Center Setup (z8nrz7f0vt): shifts, patterns, SA and UA lists. The backend
  // also opens the reads to resource_capabilities.read, for profile editors.
  resourceCenterRead: 'resource_center.read',
  resourceCenterManage: 'resource_center.manage',
  // Shift Dashboard (z8nrz7fbcj): view opens the page and its shared views;
  // manage creates, renames, re-saves and deletes those views for the tenant.
  shiftDashboardView: 'shift_dashboard.view',
  shiftDashboardManage: 'shift_dashboard.manage',
  // Write and resolve Shift notes (z8nrz7fbcq) — floor staff as well as supervisors.
  shiftDashboardNotes: 'shift_dashboard.notes',
  // My Tasks (z8nrz7fh6h). Mirrors cgt-backend src/auth/rbac/permissions.ts:
  // read opens your own tasks; read_all shows every resource and the
  // equipment queue; manage adds, assigns and moves (never without read_all).
  myTasksRead: 'my_tasks.read',
  resourceCalendarReadAll: 'resource_calendar.read_all',
  resourceCalendarManage: 'resource_calendar.manage',
  // Title21 Smart Support (cgt-backend src/support). Mirrors
  // src/auth/rbac/permissions.ts: read follows this tenant's tickets, create
  // reports an issue and replies.
  supportRead: 'support.read',
  supportCreate: 'support.create',
  fieldCategoriesRead: 'field_categories.read',
  fieldCategoriesManage: 'field_categories.manage',
  featureFlagsRead: 'feature_flags.read',
  featureFlagsManage: 'feature_flags.manage',
  tenantsRead: 'tenants.read',
  tenantsManage: 'tenants.manage',
  inventoryView: 'inventory.view',
  inventoryManage: 'inventory.manage',
  inventoryConfigure: 'inventory.configure',
  inventoryImport: 'inventory.import',
  // Purchase orders (z8nrz7f7dz). Read lists and opens them; manage raises,
  // edits, sends, receives and cancels.
  purchaseOrdersRead: 'purchase_orders.read',
  purchaseOrdersManage: 'purchase_orders.manage',
  pickListsGenerate: 'pick_lists.generate',
  pickListsPick: 'pick_lists.pick',
  // Storage unit transfer and replacement (ClickUp z8nrz7dq5k). Mirrors
  // cgt-backend/src/auth/rbac/permissions.ts — plan and execute are separate
  // keys because the approver and the technician at the freezer are not the
  // same person. storageTransfer is consumed by the mobile execution ticket.
  storageReplace: 'storage.replace',
  storageTransfer: 'storage.transfer',
  capaView: 'capa.view',
  // Action Items (z8nrz7fkfh): view opens the page, raises items and works your
  // own; manage edits and removes anyone's.
  actionItemsView: 'action_items.view',
  actionItemsManage: 'action_items.manage',
  // Product Codes (z8nrz7fvhx): the MES product catalogue, Configure → Product Code.
  productCodesRead: 'product_codes.read',
  productCodesManage: 'product_codes.manage',
  // Mirrors the backend key behind GET /products (the board's P dialog).
  productsView: 'products.view',
  // POST /shipments (z8nrz7eaex): the ship icon, the Ship button and the form action.
  productsShip: 'products.ship',
  // The label routes (z8nrz7dt48): print, verify, discard and return a label.
  productsManage: 'products.manage',
  reportsView: 'reports.view',
  alertsView: 'alerts.view',
  settingsView: 'settings.view',
  equipmentExecute: 'equipment.execute',
  // Mirrors the backend catalog (cgt-backend src/auth/rbac/permissions.ts). Both
  // have existed server-side all along; only `equipment.execute` had been
  // mirrored here, so anything gating on the other two had no key to use.
  equipmentRead: 'equipment.read',
  equipmentManage: 'equipment.manage',
} as const

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS] | (string & {})

/**
 * Maps a nav route to the permission(s) that reveal it. A user sees a menu item
 * only if they hold at least one of its permissions (full-access sees all).
 * Routes absent here (or mapped to []) are always visible.
 */
const NAV_PERMISSIONS: Record<string, string[]> = {
  '/dashboard': [PERMISSIONS.dashboardView],
  '/dashboards': [PERMISSIONS.dashboardView],
  '/shift-dashboard': [PERMISSIONS.shiftDashboardView, PERMISSIONS.shiftDashboardManage],
  '/my-tasks': [PERMISSIONS.myTasksRead, PERMISSIONS.resourceCalendarReadAll],
  '/support': [PERMISSIONS.supportRead],
  '/patients': [PERMISSIONS.submissionsRead],
  '/procedures': [PERMISSIONS.proceduresRead],
  '/procedures/calendar': [PERMISSIONS.proceduresRead],
  '/inventory': [PERMISSIONS.inventoryView],
  '/kits': [PERMISSIONS.inventoryView],
  '/inventory/pick-lists': [PERMISSIONS.inventoryView],
  '/inventory/storage-transfers': [PERMISSIONS.storageReplace],
  // The route guard's permission. Absent, the Products tile (and its tabs in
  // the main menu) showed to users the route then refused.
  '/products': [PERMISSIONS.inventoryView],
  '/capa': [PERMISSIONS.capaView],
  '/action-items': [PERMISSIONS.actionItemsView],
  '/reports': [PERMISSIONS.reportsView],
  // '/notifications' is intentionally omitted → available to any authenticated
  // user (mirrors the auth-only backend). navPermissionsFor returns [] ⇒ shown.
  '/settings': [PERMISSIONS.settingsView],
  '/form-definitions': [PERMISSIONS.formsRead],
  '/form-builder': [PERMISSIONS.formsCreate],
  '/procedure-templates': [PERMISSIONS.procedureTemplatesRead],
  '/forms': [PERMISSIONS.submissionsRead],
  '/submissions': [PERMISSIONS.submissionsRead],
  '/components': [PERMISSIONS.settingsView],
  '/integrations': [PERMISSIONS.integrationsRead],
  '/configure': [
    PERMISSIONS.usersManage,
    PERMISSIONS.rolesRead,
    PERMISSIONS.rolesManage,
    PERMISSIONS.optionSetsManage,
    // Any-of, so the Method tab is reachable by someone whose only Configure
    // business is the capability matrix — otherwise the nav hides the page that
    // holds the one tab they can use.
    PERMISSIONS.resourceCapabilitiesRead,
    // The PO template tab (z8nrz7f7dz): a buyer whose only Configure business
    // is the purchase-order template must still find the page.
    PERMISSIONS.purchaseOrdersManage,
  ],
}

// The permissions that reveal a nav target (query string ignored). [] ⇒ always shown.
export function navPermissionsFor(to: string): string[] {
  const path = to.split('?')[0]
  return NAV_PERMISSIONS[path] ?? []
}

/**
 * Permission checks in components:
 *   const { can, canAny } = usePermission()
 *   {can('users.manage') && <Button/>}
 *
 * Checks are TENANT-AWARE: a permission is resolved against the user's role in
 * the currently selected tenant, mirroring the backend PermissionsGuard. The
 * same user can be an admin in one tenant and hold nothing in another, so this
 * must be re-evaluated when the tenant switches — hence reading the selection
 * from TenantProvider (reactive) rather than localStorage.
 *
 * `permissions` on the session is only the shell baseline (dashboard + tenant
 * list); everything else lives in `tenantPermissions`.
 *
 * NOTE: global admin surfaces (Users, Roles, Tenants, Feature Flags, Sources,
 * Terminology) must gate on `isSuperAdmin`, NOT on `can(key)`. Their backend
 * routes have no tenant guard, so a tenant role carrying e.g. `tenants.manage`
 * would make `can()` true while every request behind the screen 403s.
 */
export function usePermission() {
  const { user } = useAuth()
  const { selectedTenantId } = useTenant()

  const tenantPerms =
    (selectedTenantId ? user?.tenantPermissions?.[selectedTenantId] : undefined) ?? []
  const baseline = user?.permissions ?? []
  const isSuperAdmin = user?.fullAccess ?? false

  const can = (key: string) =>
    !!user && (isSuperAdmin || tenantPerms.includes(key) || baseline.includes(key))
  const canAny = (keys: readonly string[]) =>
    !!user && (isSuperAdmin || keys.length === 0 || keys.some((k) => can(k)))

  return {
    can,
    canAny,
    roles: user?.roles ?? [],
    /** Platform-wide access. Gate global admin surfaces on this. */
    isSuperAdmin,
    /** @deprecated Use isSuperAdmin — kept so existing call sites keep compiling. */
    fullAccess: isSuperAdmin,
  }
}

/**
 * `can()` for components that must also render with no providers mounted (the
 * form renderer's standalone SSR tests): outside them it answers false, which
 * only ever HIDES a control — the server still decides every request.
 */
export function useOptionalPermission() {
  const user = useOptionalAuth()?.user ?? null
  const selectedTenantId = useOptionalTenant()?.selectedTenantId ?? null
  const tenantPerms =
    (selectedTenantId ? user?.tenantPermissions?.[selectedTenantId] : undefined) ?? []
  const can = (key: string) =>
    !!user && (user.fullAccess || tenantPerms.includes(key) || (user.permissions ?? []).includes(key))
  return { can }
}
